/**
 * Screen Recording Prevention Utility - COMPLETE V2
 * Comprehensive blocking for all screen capture methods
 * Desktop & Mobile (iOS & Android) protection
 */

export interface ScreenRecordingConfig {
  enableDetection: boolean
  enableWarnings: boolean
  blockAPIs: boolean
  blockKeyboardShortcuts: boolean
  showWarningOverlay: boolean
  logAttempts: boolean
  enableMobileProtection?: boolean
  enableIOSProtection?: boolean
  enableAndroidProtection?: boolean
  pauseVideoOnThreshold?: boolean
  courseId?: string
  lessonId?: string
}

const defaultConfig: ScreenRecordingConfig = {
  enableDetection: true,
  enableWarnings: true,
  blockAPIs: true,
  blockKeyboardShortcuts: true,
  showWarningOverlay: true,
  logAttempts: true,
  enableMobileProtection: true,
  enableIOSProtection: true,
  enableAndroidProtection: true,
  pauseVideoOnThreshold: true,
}

let recordingWarningShown: number | false = false
let recordingAttempts = 0
const recordingAttemptsThreshold = 5
let videoPlayerInstance: HTMLVideoElement | null = null
let pausedDueToRecording = false

/**
 * Detect mobile device
 */
function detectMobileDevice(): { isMobile: boolean; isIOS: boolean; isAndroid: boolean } {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !/(Windows|Android)/.test(ua)
  const isAndroid = /Android/.test(ua)
  const isMobile = /Mobile|Android|iPhone/.test(ua)

  return { isMobile, isIOS, isAndroid }
}

/**
 * Initialize screen recording prevention - COMPLETE BLOCKING
 */
export function initializeScreenRecordingPrevention(
  config: Partial<ScreenRecordingConfig> = {}
): void {
  const finalConfig = { ...defaultConfig, ...config }
  const { isMobile, isIOS, isAndroid } = detectMobileDevice()

  console.log('🔒 Initializing Screen Recording Prevention V2', {
    isMobile,
    isIOS,
    isAndroid,
  })

  // STEP 1: Block all possible APIs first
  if (finalConfig.blockAPIs) {
    blockScreenCaptureAPIs(finalConfig)
    blockScreenCapturePermissions(finalConfig)
    blockAccessibilityAPIs(finalConfig)
  }

  // STEP 2: Keyboard shortcuts blocking
  if (finalConfig.blockKeyboardShortcuts) {
    blockRecordingKeyboardShortcuts(finalConfig)
    blockDeveloperTools(finalConfig)
  }

  // STEP 3: Detection and warnings
  if (finalConfig.enableDetection) {
    detectScreenRecordingAttempts(finalConfig)
  }

  if (finalConfig.enableWarnings) {
    monitorForRecordingIndicators(finalConfig)
    monitorScreenRecordingIndicators(finalConfig)
  }

  // STEP 4: Protect video element completely
  protectVideoElement(finalConfig)
  disableVideoDownload(finalConfig)
  disableVideoInspection(finalConfig)

  // STEP 5: Apply mobile-specific protections
  if (isMobile && finalConfig.enableMobileProtection) {
    applyMobileProtections(finalConfig, isIOS, isAndroid)
  }

  // STEP 6: Universal protections
  applyUniversalProtections(finalConfig)

  // STEP 7: Log security events periodically
  startSecurityEventLogging(finalConfig)
}

/**
 * Block screen capture permissions at the browser level
 */
function blockScreenCapturePermissions(config: ScreenRecordingConfig): void {
  // Block permissions API for screen recording
  if ((navigator as any).permissions && (navigator as any).permissions.query) {
    const originalQuery = (navigator as any).permissions.query
    ;(navigator as any).permissions.query = async (params: any) => {
      if (params.name === 'display_capture') {
        recordingAttempts++
        if (config.logAttempts) {
          console.warn('🚫 Display capture permission query blocked')
        }
        return { state: 'denied', onchange: null }
      }
      return originalQuery(params)
    }
  }
}

/**
 * Block accessibility APIs that could be used for recording
 */
function blockAccessibilityAPIs(config: ScreenRecordingConfig): void {
  // Block accessibility service
  if ((window as any).AccessibilityServiceManager) {
    console.warn('🚫 Accessibility Service blocked')
    delete (window as any).AccessibilityServiceManager
  }

  // Block Android MediaProjection
  if ((window as any).MediaProjectionManager) {
    console.warn('🚫 MediaProjectionManager blocked')
    delete (window as any).MediaProjectionManager
  }
}

/**
 * Block developer tools
 */
function blockDeveloperTools(config: ScreenRecordingConfig): void {
  // Block F12, DevTools shortcuts
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && e.key === 'I')) {
      e.preventDefault()
      e.stopPropagation()
      recordingAttempts++

      if (config.logAttempts) {
        console.warn('🚫 Developer Tools access blocked')
      }
    }
  }, true)

  // Detect DevTools opening
  let devtools = { open: false }
  const threshold = 160

  setInterval(() => {
    if (window.outerHeight - window.innerHeight > threshold || window.outerWidth - window.innerWidth > threshold) {
      if (!devtools.open) {
        devtools.open = true
        recordingAttempts++

        if (config.logAttempts) {
          console.warn('🚫 DevTools opened - recording attempt detected')
        }

        showRecordingDetectionWarning()
      }
    } else {
      devtools.open = false
    }
  }, 500)
}

/**
 * Apply mobile-specific screen recording protections
 */
function applyMobileProtections(
  config: ScreenRecordingConfig,
  isIOS: boolean,
  isAndroid: boolean
): void {
  if (isIOS) {
    applyIOSProtections(config)
  }

  if (isAndroid) {
    applyAndroidProtections(config)
  }

  // Universal mobile protections
  applyUniversalMobileProtections(config)
}

/**
 * iOS-specific screen recording protections
 */
function applyIOSProtections(config: ScreenRecordingConfig): void {
  if (!config.enableIOSProtection) return

  console.log('📱 Applying iOS protections')

  // Disable swipe-up Control Center gesture
  document.addEventListener('gesturestart', (e: Event) => {
    const target = (e as any).target as HTMLElement
    if (target?.tagName === 'VIDEO' || target?.closest('[data-protected-video]')) {
      ;(e as any).preventDefault()
      recordingAttempts++

      if (config.logAttempts) {
        console.warn('🚫 iOS gesture-based recording attempt blocked')
      }
    }
  })

  // Monitor visibility changes (screenshot detection)
  const originalVisibilityChange = document.visibilityState

  document.addEventListener('visibilitychange', () => {
    if (document.hidden === false && originalVisibilityChange === 'hidden') {
      recordingAttempts++

      if (config.logAttempts) {
        console.warn('🚫 iOS screen capture attempt detected')
      }

      if (config.showWarningOverlay) {
        showRecordingDetectionWarning()
      }
    }
  })

  // Block AirPlay detection
  if ((navigator as any).mediaSession) {
    const session = (navigator as any).mediaSession
    session.playbackState = 'playing'
    session.setActionHandler('play', () => {})
    session.setActionHandler('pause', () => {})
  }

  // Block screenshot via keyboard
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'PrintScreen' || e.key === 'Screenshot') {
      e.preventDefault()
      recordingAttempts++

      if (config.logAttempts) {
        console.warn('🚫 iOS screenshot key blocked')
      }

      showRecordingDetectionWarning()
    }
  }, true)
}

/**
 * Android-specific screen recording protections
 */
function applyAndroidProtections(config: ScreenRecordingConfig): void {
  if (!config.enableAndroidProtection) return

  console.log('🤖 Applying Android protections')

  // Block MediaProjection API
  const originalGetDisplayMedia = navigator.mediaDevices?.getDisplayMedia

  if (typeof originalGetDisplayMedia === 'function') {
    navigator.mediaDevices.getDisplayMedia = async function (this: MediaDevices, ...args: any[]) {
      recordingAttempts++

      if (config.logAttempts) {
        console.warn('🚫 Android MediaProjection/getDisplayMedia blocked')
      }

      if (config.showWarningOverlay) {
        showRecordingDetectionWarning()
      }

      throw new Error('Screen recording is blocked for protected content')
    }
  }

  // Block accessibility services
  document.addEventListener('accessibilitychange', () => {
    recordingAttempts++

    if (config.logAttempts) {
      console.warn('🚫 Android accessibility service detected')
    }

    if (config.showWarningOverlay) {
      showRecordingDetectionWarning()
    }
  })

  // Block MediaStore recording intent
  if ((window as any).Intent && (window as any).Intent.ACTION_SCREEN_CAPTURE) {
    delete (window as any).Intent.ACTION_SCREEN_CAPTURE
  }
}

/**
 * Universal mobile protections (iOS & Android)
 */
function applyUniversalMobileProtections(config: ScreenRecordingConfig): void {
  // Prevent long-press context menu
  document.addEventListener('longpress', (e: Event) => {
    const target = (e as any).target as HTMLElement
    if (target?.tagName === 'VIDEO' || target?.closest('[data-protected-video]')) {
      e.preventDefault()
      recordingAttempts++

      if (config.logAttempts) {
        console.warn('🚫 Mobile long-press attempt on video blocked')
      }

      if (config.showWarningOverlay) {
        showRecordingDetectionWarning()
      }
    }
  })

  // Block touch and hold for copying
  document.addEventListener(
    'touchstart',
    (e: TouchEvent) => {
      const target = e.target as HTMLElement
      if (target?.tagName === 'VIDEO' || target?.closest('[data-protected-video]')) {
        let pressTimer: NodeJS.Timeout
        const startTouch = () => {
          pressTimer = setTimeout(() => {
            recordingAttempts++

            if (config.logAttempts) {
              console.warn('🚫 Mobile touch-hold attempt blocked')
            }

            if (config.showWarningOverlay) {
              showRecordingDetectionWarning()
            }
          }, 500)
        }

        const endTouch = () => {
          clearTimeout(pressTimer)
        }

        startTouch()
        target?.addEventListener('touchend', endTouch, { once: true })
      }
    },
    { passive: false }
  )

  // Disable double-tap zoom on video
  let lastTouchEnd = 0
  document.addEventListener(
    'touchend',
    (e: TouchEvent) => {
      const target = e.target as HTMLElement
      if (target?.tagName === 'VIDEO' || target?.closest('[data-protected-video]')) {
        const now = Date.now()
        if (now - lastTouchEnd <= 300) {
          e.preventDefault()
          recordingAttempts++

          if (config.logAttempts) {
            console.warn('🚫 Mobile double-tap zoom attempt blocked')
          }
        }
        lastTouchEnd = now
      }
    },
    { passive: false }
  )

  // Block pinch-zoom
  document.addEventListener(
    'wheel',
    (e: WheelEvent) => {
      const target = e.target as HTMLElement
      if ((e as any).ctrlKey && (target?.tagName === 'VIDEO' || target?.closest('[data-protected-video]'))) {
        e.preventDefault()
        recordingAttempts++

        if (config.logAttempts) {
          console.warn('🚫 Mobile pinch-zoom attempt blocked')
        }
      }
    },
    { passive: false }
  )
}

/**
 * Universal protections for all devices
 */
function applyUniversalProtections(config: ScreenRecordingConfig): void {
  // Prevent video from being paused by user to record individual frames
  const videoElements = document.querySelectorAll('video')
  videoElements.forEach((video) => {
    // Add watermark overlay during paused state
    video.addEventListener('pause', () => {
      const overlay = document.createElement('div')
      overlay.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 24px;
        opacity: 0.7;
        pointer-events: none;
        z-index: 1000;
      `
      overlay.textContent = 'Protected Content - Recording Disabled'
      video.parentElement?.appendChild(overlay)

      setTimeout(() => overlay.remove(), 2000)
    })
  })

  // Monitor for clipboard changes (screenshot detection)
  if ((navigator as any).clipboard) {
    document.addEventListener('paste', (e) => {
      recordingAttempts++

      if (config.logAttempts) {
        console.warn('🚫 Paste operation detected - possible screenshot attempt')
      }
    })
  }

  // Block external streaming
  if (typeof RTCPeerConnection !== 'undefined') {
    const originalRTC = RTCPeerConnection
    ;(window as any).RTCPeerConnection = class extends (originalRTC as any) {
      constructor(config: any) {
        super(config)
        recordingAttempts++

        if (config.logAttempts) {
          console.warn('🚫 WebRTC connection attempted')
        }
      }
    }
  }
}

/**
 * Detect screen recording attempts via various methods
 */
function detectScreenRecordingAttempts(config: ScreenRecordingConfig): void {
  // Block getDisplayMedia
  const originalGetDisplayMedia = navigator.mediaDevices?.getDisplayMedia

  if (typeof originalGetDisplayMedia === 'function') {
    navigator.mediaDevices.getDisplayMedia = async function (this: MediaDevices, ...args: any[]) {
      recordingAttempts++

      if (config.logAttempts) {
        console.warn(`🚫 Screen Recording Attempt #${recordingAttempts}`)
        console.warn('User attempted to use getDisplayMedia() API')
      }

      if (config.showWarningOverlay) {
        showRecordingDetectionWarning()
      }

      throw new Error('Screen recording is not permitted while watching protected course content')
    }
  }

  // Detect Canvas-based screen capture
  detectCanvasFingerprinting(config)

  // Detect tab capture
  detectTabCaptureAPI(config)

  // Detect WebGL context
  detectWebGLCapture(config)
}

/**
 * Detect canvas-based screen capture attempts
 */
function detectCanvasFingerprinting(config: ScreenRecordingConfig): void {
  const originalToDataURL = HTMLCanvasElement.prototype.toDataURL
  const originalToBlob = HTMLCanvasElement.prototype.toBlob

  HTMLCanvasElement.prototype.toDataURL = function (...args) {
    const isVideoPlayer =
      this.closest('[data-protected-video]') || this.closest('video') || this.className.includes('video')

    if (isVideoPlayer) {
      recordingAttempts++

      if (config.logAttempts) {
        console.warn('🚫 Canvas capture attempt detected (toDataURL)')
      }

      if (config.showWarningOverlay) {
        showRecordingDetectionWarning()
      }
    }

    return originalToDataURL.apply(this, args)
  }

  HTMLCanvasElement.prototype.toBlob = function (callback, ...args) {
    const isVideoPlayer =
      this.closest('[data-protected-video]') || this.closest('video') || this.className.includes('video')

    if (isVideoPlayer) {
      recordingAttempts++

      if (config.logAttempts) {
        console.warn('🚫 Canvas capture attempt detected (toBlob)')
      }

      if (config.showWarningOverlay) {
        showRecordingDetectionWarning()
      }
    }

    return originalToBlob.apply(this, [callback, ...args])
  }
}

/**
 * Detect WebGL-based screen capture
 */
function detectWebGLCapture(config: ScreenRecordingConfig): void {
  const canvas = document.createElement('canvas')
  const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')

  if (gl && typeof (gl as WebGLRenderingContext).readPixels === 'function') {
    const webgl = gl as WebGLRenderingContext
    const originalReadPixels = webgl.readPixels.bind(webgl)
    ;(webgl as any).readPixels = function (...args: any[]) {
      recordingAttempts++

      if (config.logAttempts) {
        console.warn('🚫 WebGL readPixels called - possible screen capture attempt')
      }

      if (config.showWarningOverlay) {
        showRecordingDetectionWarning()
      }

      // Cast originalReadPixels to any so apply accepts a flexible arguments array
      return (originalReadPixels as any).apply(this, args)
    }
  }
}

/**
 * Detect tab capture API usage
 */
function detectTabCaptureAPI(config: ScreenRecordingConfig): void {
  const originalGetDisplayMedia = navigator.mediaDevices?.getDisplayMedia

  if (typeof originalGetDisplayMedia === 'function') {
    navigator.mediaDevices.getDisplayMedia = async function (this: MediaDevices, ...args: any[]) {
      recordingAttempts++

      if (config.logAttempts) {
        console.warn('🚫 Tab capture API attempt detected')
      }

      if (config.showWarningOverlay) {
        showRecordingDetectionWarning()
      }

      throw new Error('Screen/Tab capture is disabled for protected content')
    }
  }
}

/**
 * Block screen capture APIs
 */
function blockScreenCaptureAPIs(config: ScreenRecordingConfig): void {
  // Block getDisplayMedia
  if (typeof navigator.mediaDevices?.getDisplayMedia === 'function') {
    const originalGetDisplayMedia = navigator.mediaDevices.getDisplayMedia
    navigator.mediaDevices.getDisplayMedia = async () => {
      recordingAttempts++

      if (config.logAttempts) {
        console.warn('🚫 getDisplayMedia() blocked')
      }

      if (config.showWarningOverlay) {
        showRecordingDetectionWarning()
      }

      throw new Error(
        'Screen recording is not permitted for this content. Contact support if you believe this is an error.'
      )
    }
  }

  // Block getUserMedia for screen sharing
  if (navigator.mediaDevices?.getUserMedia) {
    const originalGetUserMedia = navigator.mediaDevices.getUserMedia
    navigator.mediaDevices.getUserMedia = async function (constraints: MediaStreamConstraints) {
      if (
        (constraints?.video as any)?.mandatory?.chromeMediaSource === 'screen' ||
        (constraints?.video as any)?.mandatory?.chromeMediaSource === 'desktop'
      ) {
        recordingAttempts++

        if (config.logAttempts) {
          console.warn('🚫 getUserMedia() with screen source blocked')
        }

        if (config.showWarningOverlay) {
          showRecordingDetectionWarning()
        }

        throw new Error('Screen capture is not permitted')
      }

      return originalGetUserMedia.call(this, constraints)
    }
  }

  // Block WebRTC screen capture
  blockWebRTCScreenCapture(config)
}

/**
 * Block WebRTC screen capture
 */
function blockWebRTCScreenCapture(config: ScreenRecordingConfig): void {
  const originalGetSources = (navigator as any).mediaDevices?.enumerateDevices

  if (originalGetSources) {
    ;(navigator.mediaDevices as any).enumerateDevices = async function (this: any) {
      const devices = await originalGetSources.apply(this)

      // Filter out screen capture devices
      return devices.filter((device: any) => {
        const isScreenCapture =
          device.label.toLowerCase().includes('screen') ||
          device.label.toLowerCase().includes('capture') ||
          device.label.toLowerCase().includes('display')

        if (isScreenCapture && config.logAttempts) {
          console.warn('🚫 Screen capture device filtered out')
        }

        return !isScreenCapture
      })
    }
  }
}

/**
 * Block keyboard shortcuts for screen recording
 */
function blockRecordingKeyboardShortcuts(config: ScreenRecordingConfig): void {
  document.addEventListener(
    'keydown',
    (e: KeyboardEvent) => {
      // Check if video is visible
      const videoElement = document.querySelector('video[data-protected-video]') || document.querySelector('video')

      if (!videoElement || !(videoElement as HTMLElement).offsetParent) {
        return
      }

      const isCtrlOrCmd = e.ctrlKey || e.metaKey
      const isShift = e.shiftKey
      const key = e.key.toLowerCase()

      // Block recording shortcuts
      const recordingShortcuts = [
        { ctrl: true, shift: true, key: 's' }, // Shift+Ctrl+S
        { ctrl: true, shift: true, key: 'r' }, // Shift+Ctrl+R
        { meta: true, shift: true, key: '5' }, // Cmd+Shift+5
        { meta: true, shift: true, key: '6' }, // Cmd+Shift+6
        { key: 'printscreen' }, // Print Screen
        { key: 'f10' }, // F10
      ]

      for (const shortcut of recordingShortcuts) {
        const matches =
          (shortcut.ctrl ? isCtrlOrCmd : !isCtrlOrCmd || true) &&
          (shortcut.shift ? isShift : !isShift || true) &&
          key === shortcut.key

        if (matches) {
          e.preventDefault()
          e.stopPropagation()

          recordingAttempts++

          if (config.logAttempts) {
            console.warn(`🚫 Recording keyboard shortcut blocked: ${shortcut.key}`)
          }

          if (config.showWarningOverlay) {
            showRecordingDetectionWarning()
          }

          break
        }
      }
    },
    true
  )
}

/**
 * Monitor for recording indicators
 */
function monitorForRecordingIndicators(config: ScreenRecordingConfig): void {
  // Check MediaRecorder API
  const originalMediaRecorder = (window as any).MediaRecorder

  if (originalMediaRecorder) {
    ;(window as any).MediaRecorder = class extends originalMediaRecorder {
      constructor(stream: MediaStream, options?: any) {
        super(stream, options)

        const hasVideoTrack = stream.getVideoTracks().length > 0

        if (hasVideoTrack) {
          recordingAttempts++

          if (config.logAttempts) {
            console.warn('🚫 MediaRecorder initialized with video stream')
          }

          if (config.showWarningOverlay) {
            showRecordingDetectionWarning()
          }
        }
      }
    }
  }

  // Monitor for visual indicators
  checkBrowserRecordingIndicators(config)
}

/**
 * Monitor screen recording indicators
 */
function monitorScreenRecordingIndicators(config: ScreenRecordingConfig): void {
  setInterval(() => {
    try {
      // Chrome: Monitor indicator
      const hasRecorder = navigator.mediaDevices.getDisplayMedia !== undefined

      if (hasRecorder) {
        // Our block is in place
      }
    } catch (e) {
      // Silent
    }
  }, 5000)
}

/**
 * Check for browser recording indicators
 */
function checkBrowserRecordingIndicators(config: ScreenRecordingConfig): void {
  setInterval(() => {
    try {
      const haveRecorder = navigator.mediaDevices.getDisplayMedia !== undefined

      if (haveRecorder) {
        // Blocks are active
      }
    } catch (e) {
      // Silent
    }
  }, 5000)
}

/**
 * Protect video element from context menu abuse
 */
function protectVideoElement(config: ScreenRecordingConfig): void {
  document.addEventListener(
    'contextmenu',
    (e: MouseEvent) => {
      const target = e.target as HTMLElement

      if (target?.tagName === 'VIDEO' || target?.closest('video') || target?.hasAttribute('data-protected-video')) {
        e.preventDefault()
        e.stopPropagation()

        if (config.logAttempts) {
          console.warn('🚫 Right-click context menu blocked on video')
        }

        if (config.showWarningOverlay) {
          showRecordingDetectionWarning()
        }

        return false
      }
    },
    true
  )

  // Disable drag and drop of video
  document.addEventListener(
    'dragstart',
    (e: DragEvent) => {
      const target = e.target as HTMLElement

      if (target?.tagName === 'VIDEO' || target?.closest('video')) {
        e.preventDefault()
        recordingAttempts++

        if (config.logAttempts) {
          console.warn('🚫 Video drag attempt blocked')
        }
      }
    },
    true
  )
}

/**
 * Disable video download
 */
function disableVideoDownload(config: ScreenRecordingConfig): void {
  const videos = document.querySelectorAll('video')
  videos.forEach((video) => {
    video.setAttribute('controlsList', 'nodownload')
    video.style.pointerEvents = 'none'

    setTimeout(() => {
      video.style.pointerEvents = 'auto'
    }, 100)
  })
}

/**
 * Disable video inspection
 */
function disableVideoInspection(config: ScreenRecordingConfig): void {
  document.addEventListener(
    'keydown',
    (e: KeyboardEvent) => {
      if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && e.key === 'I')) {
        e.preventDefault()
        e.stopPropagation()
        recordingAttempts++

        if (config.logAttempts) {
          console.warn('🚫 Inspect element attempt blocked')
        }
      }
    },
    true
  )
}

/**
 * Start security event logging
 */
function startSecurityEventLogging(config: ScreenRecordingConfig): void {
  setInterval(() => {
    if (recordingAttempts > 0) {
      logSecurityEvent('screen_recording_attempt', config.courseId || '', config.lessonId || '')

      // Pause video after threshold
      if (config.pauseVideoOnThreshold && hasExceededRecordingAttemptThreshold()) {
        pauseVideoOnThreshold(config)
      }
    }
  }, 60000) // Every minute
}

/**
 * Pause video on threshold
 */
function pauseVideoOnThreshold(config: ScreenRecordingConfig): void {
  if (pausedDueToRecording) return

  const videoElement = document.querySelector('video') as HTMLVideoElement
  if (videoElement) {
    videoElement.pause()
    pausedDueToRecording = true

    showRecordingDetectionWarning()

    console.warn('⚠️ Video paused due to multiple recording attempts')
  }
}

/**
 * Show warning overlay
 */
function showRecordingDetectionWarning(): void {
  const now = Date.now()
  const shouldShow = recordingWarningShown ? (now - (recordingWarningShown as any)) > 10000 : true

  if (!shouldShow) {
    return
  }

  recordingWarningShown = now as any

  const warningId = 'screen-recording-warning'
  let warning = document.getElementById(warningId)

  if (!warning) {
    warning = document.createElement('div')
    warning.id = warningId
    document.body.appendChild(warning)
  }

  warning.innerHTML = `
    <div style="
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
      color: white;
      padding: 32px;
      border-radius: 16px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.4);
      z-index: 10000;
      max-width: 450px;
      text-align: center;
      font-family: system-ui, -apple-system, sans-serif;
      animation: slideIn 0.3s ease-out;
    ">
      <div style="
        font-size: 28px;
        margin-bottom: 12px;
      ">🚫 RECORDING BLOCKED</div>
      <p style="
        font-size: 14px;
        margin: 12px 0;
        line-height: 1.5;
        opacity: 0.95;
      ">
        Screen recording is disabled on this protected course content.
        This content is for your personal use only.
      </p>
      <p style="
        font-size: 13px;
        margin-top: 12px;
        opacity: 0.9;
      ">
        Attempts recorded: ${recordingAttempts} / ${recordingAttemptsThreshold}
      </p>
      ${recordingAttempts >= recordingAttemptsThreshold - 1 ? `<p style="font-size: 12px; color: #fbbf24; margin-top: 8px;">⚠️ One more attempt will pause the video</p>` : ''}
    </div>
    <style>
      @keyframes slideIn {
        from {
          opacity: 0;
          transform: translate(-50%, -60%);
        }
        to {
          opacity: 1;
          transform: translate(-50%, -50%);
        }
      }
      @keyframes fadeOut {
        to {
          opacity: 0;
          transform: translate(-50%, -50%) scale(0.95);
        }
      }
      #screen-recording-warning {
        animation: fadeOut 0.3s ease-out 3s forwards;
      }
    </style>
  `

  setTimeout(() => {
    warning?.remove()
  }, 3300)
}

/**
 * Set video as protected
 */
export function setVideoAsProtected(videoElement: HTMLVideoElement): void {
  videoElement.setAttribute('data-protected-video', 'true')
  videoElement.setAttribute('controlsList', 'nodownload')

  videoPlayerInstance = videoElement

  // Additional protections
  videoElement.style.pointerEvents = 'none'

  setTimeout(() => {
    videoElement.style.pointerEvents = 'auto'
  }, 100)
}

/**
 * Get recording attempt count
 */
export function getRecordingAttemptCount(): number {
  return recordingAttempts
}

/**
 * Reset recording attempt count
 */
export function resetRecordingAttemptCount(): void {
  recordingAttempts = 0
  recordingWarningShown = false
  pausedDueToRecording = false
}

/**
 * Check if threshold exceeded
 */
export function hasExceededRecordingAttemptThreshold(): boolean {
  return recordingAttempts >= recordingAttemptsThreshold
}

/**
 * Pause video
 */
export function pauseVideo(): void {
  if (videoPlayerInstance) {
    videoPlayerInstance.pause()
  }
}

/**
 * Resume video
 */
export function resumeVideo(): void {
  if (videoPlayerInstance && pausedDueToRecording) {
    videoPlayerInstance.play()
    pausedDueToRecording = false
  }
}

/**
 * Log security event to backend
 */
export async function logSecurityEvent(
  eventType: string,
  courseId: string,
  lessonId: string
): Promise<void> {
  try {
    const token = localStorage.getItem('token')

    if (!token || !courseId || !lessonId) {
      return
    }

    await fetch('/api/security/log-event', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        eventType,
        courseId,
        lessonId,
        attemptCount: recordingAttempts,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      }),
    })
  } catch (error) {
    console.error('Failed to log security event:', error)
  }
}

/**
 * Disable inspect on video
 */
export function disableInspectOnVideo(): void {
  document.addEventListener(
    'keydown',
    (e: KeyboardEvent) => {
      const videoElement = document.querySelector('video[data-protected-video]') || document.querySelector('video')

      if (!videoElement) {
        return
      }

      if (
        e.key === 'F12' ||
        (e.ctrlKey && e.shiftKey && e.key === 'C') ||
        (e.ctrlKey && e.shiftKey && e.key === 'I') ||
        (e.metaKey && e.altKey && e.key === 'I')
      ) {
        e.preventDefault()
        e.stopPropagation()
      }
    },
    true
  )
}
